import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Student } from 'src/app/core/schema/Student.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { StatusDotComponent, StatusDotStatus } from 'src/app/ui-kit/status-dot/status-dot.component';

/**
 * Dense list-row for a {@link Student}. Renders avatar, name, grade/school subtitle,
 * and an optional caseload status dot. For a rich roster card, use `StudentCardComponent`.
 *
 * @security FERPA — all displayed fields are student PII. Never log or transmit.
 *
 * @feature shared
 */
@Component({
  selector: 'app-student-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, IconComponent, StatusDotComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full rounded-lg text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open ' + fullName() + ' profile'"
    >
      <div class="flex items-center justify-center px-3 self-stretch">
        <app-avatar [name]="fullName()" [colorSeed]="colorSeed()" size="sm" />
      </div>

      <div class="min-w-0 flex-1 py-4 pl-1 space-y-1">
        <p class="truncate text-base font-semibold text-body">{{ fullName() }}</p>
        @if (subtitle()) {
          <p class="truncate text-sm text-muted">{{ subtitle() }}</p>
        }
      </div>

      @if (showDisabilityFlag() && hasNoDisability()) {
        <div
          class="flex items-center px-2 self-stretch"
          role="img"
          aria-label="Missing disability"
          title="Missing disability"
        >
          <app-icon name="flag" size="xs" color="danger" />
        </div>
      }

      @if (caseloadStatus()) {
        <div class="flex items-center px-3 self-stretch">
          <app-status-dot [status]="caseloadStatus()!" [ariaLabel]="caseloadStatusLabel()" />
        </div>
      }

      @if (clickable()) {
        <div class="flex items-center px-3 self-stretch">
          <app-icon name="chevron-forward-outline" size="xs" color="medium" />
        </div>
      }
    </button>
  `,
})
export class StudentListItemComponent {
  /** The student to render. @security FERPA. */
  readonly student = input.required<Student>();

  /** Optional caseload status dot shown on the right. */
  readonly caseloadStatus = input<StatusDotStatus | undefined>(undefined);

  /** Aria-label for the caseload status dot. @default 'Caseload status' */
  readonly caseloadStatusLabel = input<string>('Caseload status');

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /**
   * When true, shows a red flag on the right when the student has no
   * disability category set. Opt-in so the flag only appears on surfaces
   * that want it (e.g. the caseload students list). @default false
   */
  readonly showDisabilityFlag = input<boolean>(false);

  /** Fires when the row is clicked. */
  readonly rowClick = output<Student>();

  readonly fullName = computed(() => {
    const s = this.student();
    const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
    return name || 'Unknown student';
  });

  /**
   * Hash seed for the avatar's deterministic background color. Keyed on the
   * student id so the colour stays stable across name edits and never
   * collides between students who happen to share a name.
   */
  readonly colorSeed = computed(() => this.student().id ?? '');

  /** True when the student has no disability category set — drives a red flag. */
  readonly hasNoDisability = computed(() => !this.student().disabilityType?.length);

  readonly subtitle = computed(() => {
    const s = this.student();
    const parts: string[] = [];
    if (s.grade) parts.push(`Grade ${s.grade}`);
    if (s.school) parts.push(s.school);
    const areaCount = s.areasOfNeed?.length ?? 0;
    if (areaCount > 0) parts.push(`${areaCount} ${areaCount === 1 ? 'area' : 'areas'} of need`);
    return parts.join(' · ');
  });

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.student());
    }
  }
}
